import React from "react";

import {
    IAddon,
    IBundleGroup,
    IProduct,
    InternalProduct,
} from "../../../models/catalogue";
import { IImageURL, IProductID } from "../../../models/nominals";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { format } from "../../../utils/format";
import { ZERO } from "../../../utils/money";
import { useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import {
    baseVariantSelector,
    getUpgradeID,
    upgradeBundlesSelector,
} from "../selectors";
import { ConfiguratorUpsell } from "./ConfiguratorUpsell";
import { OptionPriceType } from "./ConfiguratorUpsells";

const buildProduct = (
    suggested_products: readonly InternalProduct[],
    selectedUpgrades: readonly IAddon[],
): InternalProduct => {
    let currentProduct = suggested_products[0];
    suggested_products.forEach((product) => {
        const upgradeID = getUpgradeID(product);
        const selectedUpgrade = selectedUpgrades.find(
            (addon) => addon.productID === upgradeID,
        );
        if (selectedUpgrade) {
            currentProduct = product;
        }
    });
    return currentProduct;
};

const isSelected = (
    upgradeID: IProductID,
    selectedUpgrades: readonly IAddon[],
): boolean => {
    const selectedUpgrade = selectedUpgrades.find(
        (addon) => addon.productID === upgradeID,
    );
    if (selectedUpgrade) {
        return true;
    }
    return false;
};

const buildUpsellProductName = (product: InternalProduct): string => {
    if (product.parent) {
        if (product.parent.attributes.upgrade_option_title) {
            return product.parent.attributes.upgrade_option_title.value;
        }
        return product.parent.title;
    }

    if (product.attributes.upgrade_option_title) {
        return product.attributes.upgrade_option_title.value;
    }

    return product.title;
};

const buildPrice = (
    product: InternalProduct,
    optionPriceType: OptionPriceType,
    baseVariant: IProduct | null,
): string => {
    if (optionPriceType === OptionPriceType.FREE_GIFT) {
        const price = getProductPrice(product.price, {
            priceType: PriceType.RETAIL,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        return `(${format.money(price)} Value)`;
    }

    let basePrice = ZERO;
    if (baseVariant && optionPriceType === OptionPriceType.RELATIVE) {
        basePrice = getProductPrice(baseVariant.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
    }
    const upgradePrice = getProductPrice(product.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    const priceDiff = upgradePrice.subtract(basePrice);
    return `+ ${format.money(priceDiff)}`;
};

const buildImage = (
    product: InternalProduct,
    bundle_group: IBundleGroup,
): IImageURL | null => {
    const bundleImage = bundle_group.image;
    if (bundleImage) {
        return bundleImage;
    }
    const productImage = product.parent
        ? product.parent.images[0]
        : product.images[0];
    if (productImage) {
        return productImage.original;
    }
    return null;
};

export const ConfiguratorUpsellContainer = (props: {
    optionPriceType: OptionPriceType;
    bundleType: string;
    selectedUpgrades: readonly IAddon[];
    onSelectionChange: (
        updates: Array<{ productID: IProductID; quantity: number }>,
    ) => void;
}): React.ReactNode => {
    const baseVariant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return baseVariantSelector(state);
    });

    const upgradeBundles = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        const variant = baseVariantSelector(state);
        return upgradeBundlesSelector(
            state.entities.concreteBundles,
            variant,
            props.bundleType,
        );
    });

    if (!baseVariant) {
        return null;
    }
    if (upgradeBundles.length <= 0) {
        return null;
    }

    const upgradeOptionElems = upgradeBundles.map((bundle) => {
        const cardProduct = buildProduct(
            bundle.suggested_products,
            props.selectedUpgrades,
        );
        const upgradeID = getUpgradeID(cardProduct);
        return (
            <ConfiguratorUpsell
                key={bundle.id}
                bundleType={props.bundleType}
                bundleID={bundle.bundle_group.id}
                sectionTitle={bundle.bundle_group.description}
                lineOne={bundle.bundle_group.headline}
                lineTwo={buildUpsellProductName(cardProduct)}
                lineThree={buildPrice(
                    cardProduct,
                    props.optionPriceType,
                    baseVariant,
                )}
                productUpgradeID={upgradeID}
                isSelected={isSelected(upgradeID, props.selectedUpgrades)}
                cardImage={buildImage(cardProduct, bundle.bundle_group)}
            />
        );
    });
    return <>{upgradeOptionElems}</>;
};
